import isempty from "lodash.isempty";

export const wait = (time: number) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve("");
    }, time);
  });

export const get_value_from_map_or_default_to_key = (map: {
  [key: string]: string;
}) => (key: string) => {
  return map[key] || key;
};

export const zip = <T>(...arrs: Array<Array<T>>) => {
  const [first_arr, ...rest] = arrs;
  return first_arr.reduce((acc, item, index) => {
    // @ts-ignore
    acc.push([item, ...rest.map((d) => d[index])]);
    return acc;
  }, []);
};

export const obj_keep_non_empty_values = (obj: any) => {
  return Object.entries(obj).reduce(function filter_for_non_empty(acc, [k, v]) {
    if (typeof v == "object" && !Array.isArray(v)) {
      acc[k] = Object.entries(v as any).reduce(filter_for_non_empty, {});
      return acc;
    } else {
      if (v) {
        acc[k] = v;
      }
      return acc;
    }
  }, {} as any);
};

export const replace_keys = (from_to: Array<[string, string]>) => (
  arr: any[]
) => {
  return arr.reduce((acc, item) => {
    from_to.forEach(([from, to]) => {
      item[to] = item[from];
      delete item[from];
    });
    acc.push(item);
    return acc;
  }, []);
};

export const record_reverse = <
  K extends string | number,
  V extends string | number
>(
  map: Record<K, V>
): Record<V, K> => {
  // @ts-ignore
  const result: Record<V, K> = {};
  for (const k in map) {
    result[map[k]] = k;
  }
  return result;
};

export const exclude_prop = (prop: string) => (o: any) => {
  return Object.entries(o).reduce((acc, [k, v]) => {
    if (k != prop) {
      // @ts-ignore
      acc[k] = v;
    }
    return acc;
  }, {});
};

export const capitalize = (str = "") => {
  const [first_letter, ...rest] = (str || "").split("");
  return [first_letter.toUpperCase(), ...rest].join("");
};

export const uniq = <T>(arr: T[]) => [...new Set(arr)];

export const uniqby = <T>(by: keyof T) => (arr: T[]) => {
  const map = new Map();
  arr.forEach((item) => map.set(item[by], item));
  return Array.from(map.values());
};

export const uniq_by_keys = <T>(...keys: Array<keyof T>) => (arr: T[]) => {
  const map = new Map();
  const create_key = (item: T) =>
    keys.reduce((acc, key) => {
      acc += "_" + item[key];
      return acc;
    }, "");
  arr.forEach((item) => map.set(create_key(item), item));
  return Array.from(map.values());
};

export const fire_once_when = <T>(
  cond: (t: T) => boolean,
  fn: (t: T) => any
) => {
  const fn_once = once(fn);
  return (t: T) => {
    if (cond(t)) {
      return fn_once(t);
    }
  };
};

export const gen_id = (prefix: string) => {
  let count = 0;
  const next_id = (): string => {
    count++;
    return `${prefix}${count}`;
  };
  return next_id;
};

export const debounce = (time: number) => (fn: (...args: any[]) => void) => {
  let taskId = 0;
  return (...args: any[]) => {
    clearTimeout(taskId);
    taskId = setTimeout(() => {
      fn(...args);
    }, time);
  };
};

export const are_2_objects_equal_by = <T>(...props: Array<keyof T>) => (
  ref_obj: T
) => (test_obj: T) => props.every((prop) => ref_obj[prop] == test_obj[prop]);

export const percentage = (total: number) => (part: number) =>
  (part * 100) / total;

export const find_last_index = <T>(predicate: (t: T) => boolean) => (
  arr: T[]
) => {
  let index = -1;
  arr.forEach((l, i) => {
    if (predicate(l)) {
      index = i;
    }
  });
  return index;
};

export const reduce = <T, R>(fn: (acc: R, item: T) => R) => (init_val: R) => (
  arr: T[]
): R => {
  return arr.reduce(fn, init_val);
};

export const normalize_booleans = (item: any) => {
  if (typeof item === "object") {
    return Object.entries(item).reduce(function normalize(acc, [key, value]) {
      acc[key] = value === "false" ? false : value === "true" ? true : value;
      return acc;
    }, {} as { [key: string]: any });
  }
  return item;
};

export const difference = (src: string[]) => (target: string[]) => {
  const src_indexed = src.reduce((acc, val) => {
    acc[val] = true;
    return acc;
  }, {} as Record<string, true>);
  target.forEach((val) => {
    delete src_indexed[val];
  });
  return Object.keys(src_indexed);
};

export const create_counter = (seed = 0) => {
  let count = seed;
  return () => {
    count++;
    return count;
  };
};

export const diff = <T, S extends keyof T>(p: {
  by: S;
  ref_arr: T[];
  diffArr: T[];
}): T[] => {
  const { by, ref_arr, diffArr } = p;
  const res: any[] = [];
  ref_arr.forEach((item, index) => {
    if (item[by] !== diffArr[index][by]) {
      res.push(diffArr[index]);
    }
  });
  return res;
};

export const prop = <T, S extends keyof T>(property: S) => (obj: T): T[S] => {
  return obj[property];
};

export const head = <T>(arr: T[]) => arr[0];

export const tail = <T>(arr: T[]) => arr[arr.length - 1];

export const pluck = <T>(...properties: Array<keyof T>) => (
  obj: T
): Partial<T> => {
  return properties.reduce((acc, prop) => {
    acc[prop] = obj[prop];
    return acc;
  }, {} as Partial<T>);
  /*  const _obj: Partial<T> = {};
  properties.forEach((prop) => (_obj[prop] = obj[prop]));
  return _obj; */
};

export const throttle_arr_exec = <T>(time: number, length: number) => (
  execFn: (items: T[]) => void
) => {
  let arr: T[] = [];
  let id = 0;
  const add = (t: T) => {
    arr.push(t);
    clearTimeout(id);
    if (arr.length >= length) {
      execFn([...arr]);
      arr = [];
    } else {
      id = setTimeout(() => {
        execFn([...arr]);
        arr = [];
      }, time);
    }
  };
  return {
    add,
  };
};

export const objToTuples = (obj: any) => Object.entries(obj);

export const objToMap = (obj: any) => new Map(objToTuples(obj));

export const indexBy = <T>(prop: keyof T) => (arr: T[]) =>
  arr.reduce((acc, t) => {
    // @ts-ignore
    acc[t[prop]] = t;
    return acc;
  }, {} as { [key: string]: T });

export const debounceVar = (timeFn: () => number) => (
  fn: (...args: any[]) => void
) => {
  let taskId = 0;
  return (...args: any[]) => {
    clearTimeout(taskId);
    taskId = setTimeout(() => {
      fn(...args);
    }, timeFn());
  };
};

export const not = <A>(fn: (arg: A) => boolean) => (a: A): boolean => !fn(a);

export const filter = <IN, OUT>(condition: (i: IN) => boolean) => (
  fn: (a: IN) => OUT
) => {
  return (arg: IN) => {
    if (condition(arg)) {
      return fn(arg);
    }
  };
};

export const firstItemOr = <T>(defaultVal: T) => (arr: T[] = []) => {
  return arr && arr.length > 0 ? arr[0] : defaultVal;
};

export const setAsFirstItem = <T>(by: (item: T) => boolean, arr: T[]) => {
  const arrCopy = [...arr];
  const fromIndex = arrCopy.findIndex((item) => by(item));
  if (fromIndex > -1) {
    // @ts-ignore
    const from = arrCopy.splice(fromIndex, 1);
    const res = [from[0], ...arrCopy];
    return res;
  } else {
    return arr;
  }
};

export const setAsLastItem = <T>(by: (item: T) => boolean, arr: T[]) => {
  const arrCopy = [...arr];
  const fromIndex = arrCopy.findIndex((item) => by(item));
  if (fromIndex > -1) {
    // @ts-ignore
    const from = arrCopy.splice(fromIndex, 1);
    const res = [...arrCopy, from[0]];
    return res;
  } else {
    return arr;
  }
};

export const switchMap = <IN, OUT>(fn: (arg: IN) => Promise<OUT>) => {
  let lastTaskId = 0;

  const exec = async (arg: IN) => {
    const id = ++lastTaskId;
    const result: OUT = await fn(arg);
    return { id, result, input: arg };
  };

  return (arg: IN): Promise<OUT> => {
    return new Promise((resolve) => {
      exec(arg).then(({ id, result }) => {
        if (id === lastTaskId) {
          resolve(result);
        }
      });
    });
  };
};

export function groupBy<T>(
  prop: keyof T,
  arr: readonly T[] | T[]
): Map<T[keyof T], T[]> {
  const map = new Map<T[keyof T], T[]>();
  arr.forEach((item) => {
    const key = item[prop];
    map.set(key, [...(map.get(key) || []), item]);
  });
  return map;
}

export function group_count_by<T>(
  count_by: (t: T) => string,
  arr: T[]
): Record<string, number> {
  return arr.reduce((acc, item) => {
    const group_id = count_by(item);
    if (acc[group_id]) {
      acc[group_id] += 1;
    } else {
      acc[group_id] = 1;
    }
    return acc;
  }, {} as Record<string, number>);
}
export function groupByObj<T, K extends keyof T>(
  prop: K,
  arr: readonly T[] | T[]
): Record<string | number, T[]> {
  // @ts-ignore
  const obj: Record<K, T[]> = {};
  arr.forEach((item) => {
    const key = item[prop];
    // @ts-ignore
    obj[key] = obj[key] ? [...obj[key], item] : [item];
  });
  // @ts-ignore
  return obj;
}

export function isNullOrUndefined(val: any): boolean {
  return val === null || val === undefined;
}

export function isEmpty(val: any): boolean {
  return isempty(val);
}

export function always<T>(val: T) {
  return () => val;
}

export const identity = <T>(val: T) => val;

export function isNotEmpty(val: any): boolean {
  return !isEmpty(val);
}

export const queue_fns = () => {
  const queue: Array<() => void> = [];
  const add = (fn: () => void) => {
    queue.push(fn);
  };
  const run = () => queue.forEach((fn) => fn());
  return {
    add,
    run,
  };
};

export const both_exist_and_not_equal = (a: any, b: any) =>
  isNotEmpty(a) && isNotEmpty(b) && a !== b;

export const exec_if_both_exist_and_not_equal = (fn: Function) => (
  a: any,
  b: any
) => {
  if (both_exist_and_not_equal(a, b)) {
    fn();
  }
};

export function clone<T>(obj: T, deep = true): T {
  try {
    if (deep) {
      // @ts-ignore
      if (window.structuredClone) {
        // @ts-ignore
        return window.structuredClone(obj);
      }
      return JSON.parse(JSON.stringify(obj));
    } else {
      return { ...obj };
    }
  } catch (err) {
    console.error("error while attempting object clone", err);
    return obj;
  }
}

export const count_by = <T, K extends keyof T>(prop: K) => (items: T[]) => {
  return items.reduce((acc, item) => {
    const current = acc.get(item[prop]) || 0;
    if (current) {
      acc.set(item[prop], current + 1);
    } else {
      acc.set(item[prop], 1);
    }
    return acc;
  }, new Map() as Map<T[K], number>);
};

export const pluckFilter = <T>(
  filter: (testItem: T, key: keyof T) => boolean
) => (keys: Array<keyof T>) => (obj: T) => {
  const o: any = {};
  for (const key in obj) {
    if (keys.includes(key) && filter(obj, key)) {
      o[key] = obj[key];
    }
  }
  return o;
};

export const onNewValueOnly = <T>(
  equalChecker: (t1: T, t2: T) => boolean,
  cb: (t1: T, t2: T) => void
) => (t1: T, t2: T) => {
  if (!equalChecker(t1, t2)) {
    cb(t1, t2);
  }
};

export function emptyFn() {
  return () => {
    /*   */
  };
}

export function delayBy(time = 2000) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve("");
    }, time);
  });
}

export const once = (fn: Function) => {
  let isFired = false;
  return (...args: any) => {
    if (isFired) {
      return;
    }
    isFired = true;
    return fn(...args);
  };
};

export const exec_only_if_cond_changed = <T, S, R>(
  cond_fn: (t: T) => Promise<S>,
  logic_fn: (t: T) => Promise<R>
) => {
  let last_cond_result: S | null = null;
  let last_result: R | null = null;
  return async (t: T) => {
    const cond = await cond_fn(t);
    if (cond !== last_cond_result) {
      last_cond_result = cond;
      last_result = await logic_fn(t);
      return last_result;
    }
    return last_result;
  };
};

export const once_ready = (fn: Function) => {
  let is_fired = false;
  const resolvers: Array<Function> = [];
  const on_ready = (): Promise<void> => {
    return new Promise((resolve) => {
      if (is_fired) {
        resolve();
      } else {
        resolvers.push(resolve);
      }
    });
  };
  const init = (...args: any) => {
    if (is_fired) {
      return;
    }
    fn(...args);
    resolvers.forEach((fn) => fn());
    is_fired = true;
  };
  return [init, on_ready];
};

export const take_first_with_value = <T>(...vals: T[]) => {
  return vals.find((d) => isNotEmpty(d))!;
};

export const once_ready_async = (fn: Function) => {
  let is_fired = false;
  const resolvers: Array<Function> = [];
  const on_ready = (): Promise<void> => {
    return new Promise((resolve) => {
      if (is_fired) {
        resolve();
      } else {
        resolvers.push(resolve);
      }
    });
  };
  const init = async (...args: any) => {
    if (is_fired) {
      return;
    }
    await fn(...args);
    resolvers.forEach((fn) => fn());
    is_fired = true;
  };
  return [init, on_ready];
};

export const always_true = always(true);

export const singleton = <IN, OUT>(fn: (input: IN) => OUT) => {
  let product: OUT;
  return (input: IN) => {
    if (product) {
      return product;
    }
    product = fn(input);
    return product;
  };
};

export const cache = <K, V>(defaultVal: V) => {
  const cache: Map<K, V> = new Map();
  const store = (k: K, v: V) => cache.set(k, v);
  return (view: K): { store: (k: K, v: V) => void; val: V } => {
    if (cache.has(view)) {
      return { store, val: cache.get(view) as V };
    } else {
      return { store, val: defaultVal };
    }
  };
};

export const gt = (min: number) => (testVal: number) => testVal > min;
export const gte = (min: number) => (testVal: number) => testVal >= min;
export const lt = (max: number) => (testVal: number) => testVal < max;
export const lte = (max: number) => (testVal: number) => testVal <= max;
export const eq = <T>(n: T) => (t: T) => t == n;

export const or = <T>(...conds: Array<(base: T) => boolean>) => (val: T) =>
  conds.some((check) => check(val));

export const and = <T>(...conds: Array<(base: T) => boolean>) => (val: T) =>
  conds.every((check) => check(val));

export const length = <T extends { length: number }>(item: T) => item.length;

export const size = <T extends { size: number }>(item: T) => item.size;

export const map = <T, A>(fn: (t: T, index?: number) => A) => (arr: T[]) =>
  arr.map(fn);

export const range = (from: number, to: number, step: number) => {
  const arr: number[] = [];
  for (let i = from; i <= to; i += step) {
    arr.push(i);
  }
  return arr;
};

export const fillArray = <T>(p: {
  count: number;
  seed: any;
  mapper: (seed: any, index: number) => T;
}) => {
  return Array(p.count)
    .fill(p.seed)
    .map(p.mapper);
};

export const completeArrTo = (completeTo: number, defaultVal: any) => (
  arr: any[]
) => {
  const arr2 = Array(completeTo).fill(defaultVal);
  arr.forEach((item, index) => arr2.splice(index, 1, item));
  return arr2;
};

export const pipe2 = <ARG, A, B>(fnA: (arg: ARG) => A, fnB: (a: A) => B) => {
  return (arg: ARG) => fnB(fnA(arg));
};

export const asyncPipe2 = <ARG, A, B>(
  fnA: (arg: ARG) => Promise<A>,
  fnB: (a: A) => Promise<B>
) => {
  return async (arg: ARG) => await fnB(await fnA(arg));
};

export const pipe3 = <ARG, A, B, C>(
  fnA: (arg: ARG) => A,
  fnB: (a: A) => B,
  fnC: (b: B) => C
) => {
  return (arg: ARG) => fnC(fnB(fnA(arg)));
};

export const asyncPipe3 = <ARG, A, B, C>(
  fnA: (arg: ARG) => Promise<A>,
  fnB: (a: A) => Promise<B>,
  fnC: (b: B) => Promise<C>
) => {
  return async (arg: ARG) => await fnC(await fnB(await fnA(arg)));
};

export const pipe4 = <ARG, A, B, C, D>(
  fnA: (arg: ARG) => A,
  fnB: (a: A) => B,
  fnC: (b: B) => C,
  fnD: (c: C) => D
) => {
  return (arg: ARG) => fnD(fnC(fnB(fnA(arg))));
};

export const asyncPipe4 = <ARG, A, B, C, D>(
  fnA: (arg: ARG) => Promise<A>,
  fnB: (a: A) => Promise<B>,
  fnC: (b: B) => Promise<C>,
  fnD: (c: C) => Promise<D>
) => {
  return async (arg: ARG) => await fnD(await fnC(await fnB(await fnA(arg))));
};

export const pipe5 = <ARG, A, B, C, D, E>(
  fnA: (arg: ARG) => A,
  fnB: (a: A) => B,
  fnC: (b: B) => C,
  fnD: (c: C) => D,
  fnE: (d: D) => E
) => {
  return (arg: ARG) => fnE(fnD(fnC(fnB(fnA(arg)))));
};

export const asyncPipe5 = <ARG, A, B, C, D, E>(
  fnA: (arg: ARG) => Promise<A>,
  fnB: (a: A) => Promise<B>,
  fnC: (b: B) => Promise<C>,
  fnD: (c: C) => Promise<D>,
  fnE: (d: D) => Promise<E>
) => {
  return async (arg: ARG) =>
    await fnE(await fnD(await fnC(await fnB(await fnA(arg)))));
};

export const isEqualBy = <T>(by: Array<keyof T>) => (t1: T, t2: T): boolean =>
  by.every((key) => t1 && t2 && t1[key] === t2[key]);

export const isEqualArrayBy = <T, S extends keyof T>(
  arr1: T[] | readonly T[] = [],
  arr2: T[] | readonly T[] = []
) => (key: S): boolean => {
  const _ids1 = [...arr1]
    .map((d) => d[key])
    .sort()
    .join(",");
  const _ids2 = [...arr2]
    .map((d) => d[key])
    .sort()
    .join(",");
  return _ids1 == _ids2;
};

export const sequence = (...fns: Function[]) => () => fns.forEach((fn) => fn());

export const sortBy = <T, S extends keyof T>(
  arr: T[],
  by: S,
  asc: boolean
): T[] => {
  arr = arr.filter((t) => t);
  const keys = arr.map((a) => a[by]).sort();
  const copy = [...arr];

  const sortedArr: T[] = keys.map((key) => {
    const index = copy.findIndex((item) => item[by] === key);
    const [item] = copy.splice(index, 1);
    return item;
  });

  return asc ? sortedArr.reverse() : sortedArr;
};

export const memoize = <A, T>(fn: (a: A) => T) => {
  const cache: Map<A, T> = new Map();
  return (a: A) => {
    if (cache.has(a)) {
      console.log("from cache");
      return cache.get(a) as T;
    } else {
      const res = fn(a);
      cache.set(a, res);
      return res;
    }
  };
};

export const order_list_by = <T, K extends keyof T>(
  key: K,
  the_list: T[K][]
) => {
  const pos: Record<string, number> = the_list.reduce((acc, key, index) => {
    // @ts-ignore
    acc[key] = index;
    return acc;
  }, {} as Record<string, number>);

  return (the_list: T[]): T[] => {
    return the_list.sort((a, b) => {
      // @ts-ignore
      const a_pos = pos[a[key]] || 0;
      // @ts-ignore
      const b_pos = pos[b[key]] || 0;
      return a_pos - b_pos;
    });
  };
};

export const memoize_ttl = <A, T>(ttl_sec: number, fn: (a: A) => T) => {
  const cache: Map<A, { val: T; exp: number }> = new Map();
  return (a: A) => {
    if (cache.has(a) && cache.get(a)!.exp > Date.now()) {
      return cache.get(a)!.val;
    } else {
      const res = fn(a);
      cache.set(a, { val: res, exp: Date.now() + ttl_sec * 1000 });
      return res;
    }
  };
};

export const memoize_by = <A, T>(by: (a: A) => any, fn: (a: A) => T) => {
  const cache: Map<A, T> = new Map();
  return (a: A) => {
    const key = by(a);
    if (cache.has(key)) {
      console.log("from memoized cache");
      return cache.get(key) as T;
    } else {
      const res = fn(a);
      cache.set(key, res);
      return res;
    }
  };
};

export const sortByMany = <T, S extends keyof T>(
  arr: T[],
  conds: Array<[S, boolean]>
): T[] => {
  return conds.reduce((result, [key, asc]) => {
    return sortBy(result, key, asc);
  }, arr);
};

export const deepProp = (path: string) => (obj: any) => {
  if (!obj) {
    return undefined;
  }
  let has_all_props = true;
  const result = path.split(".").reduce((acc, prop) => {
    if (acc[prop]) {
      acc = acc[prop];
    } else {
      has_all_props = false;
    }
    return acc;
  }, obj);
  if (has_all_props) {
    return result;
  } else {
    return undefined;
  }
};

export const filterDeepProp = <T>(propPath: string, val: any) => {
  const getProp = deepProp(propPath);
  return (arr: T[]): T[] => {
    return arr.filter((s: any) => getProp(s) === val);
  };
};

export const makeItemLast = <T>(arr: T[], fn: (it: T) => boolean) => {
  const copy = [...arr];
  const indexOfItem = arr.findIndex(fn);
  const theItem = arr[indexOfItem];
  copy.splice(indexOfItem, 1);
  copy.push(theItem);
  return copy;
};

type ExecFn<FN> = (fn: FN) => void;
export function execLast<FN>(conds: ExecFn<FN>[]) {
  return (cb: FN) => {
    const total = conds.length;
    let current = 1;
    const mock = () => ++current;
    for (const cond of conds) {
      if (current < total) {
        // @ts-ignore
        cond(mock);
      } else {
        cond(cb);
      }
    }
  };
}

export const ifElse = <IN, OUT>(condition: (input: IN) => boolean) => (
  fn1: (input: IN) => OUT,
  fn2: (input: IN) => OUT
) => (input: IN) => {
  if (condition(input)) {
    return fn1(input);
  } else {
    return fn2(input);
  }
};

/**
 * @description get the property from the object path
 * and on any exception return the value from defaultValFn()
 *
 * - use $<index> , ex : $10 for array indexes.
 * - automatically tries to parse string to json if there is more keys to process
 * and the current value is of type string.
 *
 * ## Example
 *  ```js
 * const street_name = path("user.address.street.name");
 * const erez = {user:{address:{street:{name:'Yefet',num:15},city:"Jerusalem"},langs:['js','elixir']}}
 * const erez_street = street_name(erez);
 * // 'Yefet'
 *
 * const user_first_lang = path("user.langs.$0");
 * const erez_first_lang = user_first_lang(erez);
 * // 'js'
 * ```
 *
 * */
export const path = <T>(propPath: string, defaultValFn: () => T) => (
  obj: any
): T => {
  try {
    const arr = propPath.split(".");
    const val: T = arr.reduce((acc, key, index) => {
      if (key[0] === "$") {
        const index = key.substr(1);
        return acc[index];
      }
      if (typeof acc === "string" && index < arr.length - 1) {
        const v = JSON.parse(acc);
        return v[key];
      } else {
        return acc[key];
      }
    }, obj);
    return val;
  } catch (e) {
    return defaultValFn();
  }
};

export const is_path_eq = (path_str: string, val: any) => (
  obj: any
): boolean => {
  const res = path(path_str, () => false)(obj);
  if (res) {
    return res === val;
  } else {
    return false;
  }
};

// @ts-ignore
window.is_path_eq = is_path_eq;

export const has_path = (path_str: string) => (obj: any) => {
  const res = path(path_str, () => false)(obj);
  if (res) {
    return true;
  } else {
    return false;
  }
};

export const index_by_path = <T, K extends keyof T>(
  prop_path: string,
  def_val: T[K]
) => {
  return (arr: T[]): Record<string, T> => {
    const path_to_prop = path<T[K]>(prop_path, () => def_val);
    return arr.reduce((acc, item) => {
      // @ts-ignore
      acc[path_to_prop(item)] = item;
      return acc;
    }, {} as Record<string, T>);
  };
};

export const parse = <T>(defaultVal: T) => (obj: any): T => {
  try {
    const val: T = JSON.parse(obj);
    return val;
  } catch (e) {
    return defaultVal;
  }
};

export const split = (by: string) => (str: string): string[] => str.split(by);

export const takeIndex = (index: number) => <T>(arr: T[]) => arr[index];

export const flatTreeBy = (
  by: string,
  parentChildMapper: (parent: any) => (child: any) => any
) =>
  function flatObj(obj: any): any[] {
    const arr: any[] = [];
    const getChildren = (o: any) => {
      if (o[by] && o[by].length > 0) {
        const parentMapper = parentChildMapper(o);
        const children = o[by].map(parentMapper).map((child: any) => {
          if (child[by] && child[by].length > 0) {
            return getChildren(child);
          }
          return child;
        });
        arr.push(...children);
        delete o[by];
      }
      arr.push(o);
    };
    getChildren(obj);
    return arr.flat(4).filter((t) => t);
  };

export const toRecord = <T, S extends keyof T, K extends keyof T>(
  t: T[],
  key: S,
  val: K
  // @ts-ignore
): Record<T[S], T[K]> => {
  // @ts-ignore
  return t.reduce((acc: Record<T[S], T[K]>, item: T) => {
    acc[item[key]] = item[val];
    return acc;
    // @ts-ignore
  }, {} as Record<T[S], T[K]>);
};

export const exec_later = <IN, OUT>(
  fn: (input: IN) => OUT,
  arg: IN
) => (): OUT => fn(arg);

export const exec = <OUT>(fn: () => OUT) => fn();

export const surface_prop = (path: string, new_prop: string) => (obj: any) => {
  const value = path.split(".").reduce((current_val, current_key) => {
    return current_val[current_key];
  }, obj);
  return {
    ...obj,
    [new_prop]: value,
  };
};

export const is_eq_by_props = (props: string[]) => (
  o1: any,
  o2: any
): boolean => {
  return props.every((prop) => o1[prop] == o2[prop]);
};

export const prop_from_prop = (old_prop: string, new_prop: string) => (
  obj: any
) => {
  return Object.assign({ [new_prop]: obj[old_prop] }, obj);
};

export const from_parsed = <T>(prop: string, default_val: T) => (
  str: string
) => {
  try {
    const obj: any = JSON.parse(str);
    return obj[prop];
  } catch (e) {
    console.log("from_parsed parsing error:", e);
    return default_val;
  }
};

export const merge_by = <T, K extends keyof T>(prop: K) => (arr_base: T[]) => (
  ...arr_alts: Partial<T>[][]
) => {
  const to_obj = indexBy<T | Partial<T>>(prop);
  const obj_base = to_obj(arr_base);

  for (const key in obj_base) {
    arr_alts.forEach((alt_arr) => {
      obj_base[key] = {
        ...obj_base[key],
        ...to_obj(alt_arr)[key],
      };
    });
  }
  return Object.values(obj_base);
};

export const reduce_and_merge_by_fn = <T, K extends keyof T>(
  prop: K,
  merge_fn: (a: T, b: T) => T
) => (arr: T[]): T[] => {
  const r = arr.reduce((acc, item) => {
    const key: T[K] = item[prop];
    if (acc[key]) {
      acc[key] = merge_fn(acc[key], item);
    } else {
      acc[key] = item;
    }
    return acc;
    // @ts-ignore
  }, {} as Record<T[K], T>);
  return Object.values(r);
};

export const getItemAtIndex = <T>(index: number, defaultVal: T) => (
  items: T[]
) => {
  if (items && items[index]) {
    return items[index];
  } else {
    return defaultVal;
  }
};

export const remove_direct_key = (key: string, obj: any) => {
  const cloned = clone(obj, true);
  delete cloned[key];
  return cloned;
};

export const chunk = <T>(itemsInChunk: number, arr: T[]) => (
  chunkNum: number
): T[] => {
  chunkNum = chunkNum === 0 ? 1 : chunkNum;
  const from = (chunkNum - 1) * itemsInChunk;
  const to = from + itemsInChunk;
  return arr.slice(from, to);
};

export const to_chunks = <T>(itemsInChunk: number) => (arr: T[]) => {
  let copy: T[][] = [];
  let items_left = arr.length;
  let current_chunk = 0;
  while (items_left > 0) {
    items_left -= itemsInChunk;
    const start = current_chunk * itemsInChunk;
    const end = start + itemsInChunk;
    const chunk: T[] = arr.slice(start, end);
    copy = [...copy, chunk];
    current_chunk++;
  }
  return copy;
};

export const reducers = {
  sumProp: <T>(prop: keyof T) => (acc: number, item: T): number => {
    // @ts-ignore
    acc += item[prop] as number;
    return acc;
  },
};

export const strSearch = (str: string, caseSensitive: false) => {
  if (!caseSensitive) {
    str = str.toLowerCase();
  }
  return function performSearch(test: string): boolean {
    if (caseSensitive) {
      return test.search(str) > -1;
    } else {
      return test.toLowerCase().search(str) > -1;
    }
  };
};

export const aliasProp = (prop: string, alias: string) => (obj: any) => {
  const copy = { ...obj };
  copy[alias] = copy[prop];
  return copy;
};

export const create_tree_by = <T>(
  id_prop: keyof T,
  parent_id_prop: keyof T
) => (items: T[]): Array<[T, T[]]> => {
  const children_only = items.filter((l) => l[parent_id_prop]);
  const parents_ids = children_only.map((l) => l[parent_id_prop]);

  const groups = groupBy<T>(parent_id_prop, children_only);
  const groups_as_arr = Array.from(groups.entries());

  const tree = groups_as_arr.reduce((acc, [parent_id, vals]) => {
    const parent = items.find((l) => l[id_prop] === parent_id) as T;
    acc.push([parent, vals]);
    return acc;
  }, [] as Array<[T, T[]]>);

  // add items which are not parents but also doesn't have parents
  // in the list to the tree as parents without children
  const children_with_no_parents = items.filter(
    (l) => !l[parent_id_prop] && !parents_ids.includes(l[id_prop])
  );
  children_with_no_parents.forEach((child) => {
    tree.push([child, []]);
  });

  return tree;
};

export const both = <T>(cond1: (t: T) => boolean, cond2: (t: T) => boolean) => (
  val: T
) => cond1(val) && cond2(val);

export const has_prop = <T>(prop: keyof T) => (obj: T) => {
  if (isEmpty(obj)) {
    console.warn("has_prop , obj is undefined or null !");
    return false;
  } else {
    // @ts-ignore
    return prop in obj;
  }
};

export const prop_is = <T, K extends keyof T>(
  prop: K,
  fn: (val: T[K]) => boolean
) => {
  const exist = has_prop<T>(prop);
  return (obj: T) => {
    return exist(obj) && fn(obj[prop]);
  };
};

export const prop_eq = <T, K extends keyof T>(prop: K, val: T[K]) => {
  const exist = has_prop<T>(prop);
  return (obj: T) => {
    return exist(obj) && obj[prop] == val;
  };
};

const arr_exist_and_has_children = <T, K extends keyof T>(prop: K) =>
  both(
    has_prop<T>(prop),
    // @ts-ignore
    prop_is(prop, (val) => Array.isArray(val) && val.length > 0)
  );

export const flat_by = <T, K extends keyof T>(
  prop: K,
  set_on_parent: (t: T) => T
) => {
  const has_children = arr_exist_and_has_children<T, K>(prop);
  const res: T[] = [];
  return function flat_children(arr: T[]): T[] {
    for (const item of arr) {
      if (has_children(item)) {
        // @ts-ignore
        const children: T[] = item[prop];
        delete item[prop];
        res.push(set_on_parent(item));
        flat_children(children);
      } else {
        res.push(item);
      }
    }
    return res;
  };
};

export const child_to_parent_tree_injection = (
  prop: string,
  fn: (item: any, parent: any) => any
) =>
  function work_on_tree(tree: any) {
    for (let entry of tree) {
      if (entry[prop] && entry[prop].length > 0) {
        for (let inner_item of entry[prop]) {
          if (inner_item[prop] && inner_item[prop].length > 0) {
            inner_item = work_on_tree([inner_item]);
            entry = fn(inner_item[0], entry);
          } else {
            entry = fn(inner_item, entry);
          }
        }
      }
    }
    return tree;
  };

export const includes_all = <T>(arr1: T[]) => (
  arr2: T[],
  by_prop_val?: (t: T) => any
) => {
  if (by_prop_val) {
    const indexed_arr2 = arr2.reduce((acc, item) => {
      // @ts-ignore
      acc[by_prop_val(item)] = true;
      return acc;
    }, {});
    return arr1.every((el) => by_prop_val(el) in indexed_arr2);
  } else {
    return arr1.every((el) => arr2.includes(el));
  }
};

export const rand_floor = (num: number) => Math.floor(Math.random() * num);

export const AppFp = {
  if_arr_gt_0_take_first: <T>(prop: string, default_val: T) => (
    arr: any[]
  ): T => {
    if (arr.length > 0) {
      return arr[0][prop] as T;
    } else {
      return default_val;
    }
  },
};

export const upsert_by = <T>(prop: keyof T) => <T>(item: T, arr: T[]): T[] => {
  // @ts-ignore
  if (arr.findIndex((d) => d[prop] === item[prop]) > -1) {
    return arr.map((d) => {
      // @ts-ignore
      if (d[prop] == item[prop]) {
        return item;
      }
      return d;
    });
  }
  return arr.concat(item);
};

export const omit = <T extends Record<string, any>>(...props: (keyof T)[]) => (
  o: T
): Omit<T, keyof typeof props> => {
  return Object.keys(o).reduce((acc, key) => {
    if (!props.includes(key as keyof T)) {
      // @ts-ignore
      acc[key] = o[key];
    }
    return acc;
  }, {} as Omit<T, keyof typeof props>);
};

export const pick = <T extends Record<string, any>>(...props: (keyof T)[]) => (
  o: T
): Partial<T> => {
  return Object.keys(o).reduce((acc, key) => {
    if (props.includes(key as keyof T)) {
      // @ts-ignore
      acc[key] = o[key];
    }
    return acc;
  }, {} as Partial<T>);
};
